import { Button, Checkbox, FormHelperText, TextField } from "@material-ui/core";
import { isEmptyOrWhitespace, KeyValuePair } from "@shoothill/core";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";

import { DashedSectionSplitter } from "Components/Form/SectionSplitter";
import { BaseModal } from "Components/Modal/BaseModal";
import { SnackBar } from "Components/SnackBar/SnackBar";
import { CancelButton } from "Globals/Styles/Control/Buttons";
import { AddPlanOfWorkModalStyle } from "Views/Project/Modals/Modal.Styles";
import { FormModalSection, FormRow } from "Views/Stock/Shared/Shared.styles";
import { IDrawing, IssueItemViewModel, IssueOutputDisplayName, IssueTable, IssueViewModel, OutputDrawing, OutputDrawingContainer } from ".";
import { DownloadDrawingView } from "../../Shared";
import { MultiSelectView } from "Components/Select/MultiSelect";

interface IProps {
    viewModel: IssueViewModel;
}

export const IssueView: React.FC<IProps> = observer((props) => {
    const viewModel = props.viewModel;

    const [selectedToProjectDirectoryPersonUserNames, setSelectedToProjectDirectoryPersonUserNames] = useState<string>("None"); // Display only
    const [selectedCCProjectDirectoryPersonUserNames, setSelectedCCProjectDirectoryPersonUserNames] = useState<string>("None"); // Display only

    const handleSetSelectedToProjectDirectoryPersonUsers = (val: any) => {
        viewModel.setSelectedToProjectDirectoryPersonUsers(val);
        const options: KeyValuePair[] = viewModel.getProjectDirectoryPersonUserOptions;

        const selectedNames: string[] = options.filter((o) => val.includes(o.value)).map((o) => o.key);

        if (selectedNames.length > 0) {
            setSelectedToProjectDirectoryPersonUserNames(selectedNames.join(", "));
        } else {
            setSelectedToProjectDirectoryPersonUserNames("None");
        }
    };

    const handleSetSelectedCCProjectDirectoryPersonUsers = (val: any) => {
        viewModel.setSelectedCCProjectDirectoryPersonUsers(val);
        const options: KeyValuePair[] = viewModel.getProjectDirectoryPersonUserOptions;

        const selectedNames: string[] = options.filter((o) => val.includes(o.value)).map((o) => o.key);

        if (selectedNames.length > 0) {
            setSelectedCCProjectDirectoryPersonUserNames(selectedNames.join(", "));
        } else {
            setSelectedCCProjectDirectoryPersonUserNames("None");
        }
    };

    const getProjectDirectoryPersonUserOptions = (includeDeleted: boolean): KeyValuePair[] => {
        return viewModel.getProjectDirectoryPersonUserOptions === undefined ? [] : viewModel.getProjectDirectoryPersonUserOptions.slice();
    };

    return (
        <BaseModal
            open={viewModel !== null}
            onClose={viewModel.cancel}
            title={viewModel.title}
            PaperComponent={AddPlanOfWorkModalStyle}
            actions={
                <React.Fragment>
                    <Button autoFocus color="secondary" disabled={viewModel.server.IsBusy || viewModel.IsLoading} onClick={viewModel.apiIssueAsync} variant="contained">
                        Send
                    </Button>
                    <CancelButton disabled={viewModel.server.IsBusy || viewModel.IsLoading} onClick={viewModel.cancel}>
                        Cancel
                    </CancelButton>
                </React.Fragment>
            }
        >
            <FormModalSection>
                <IssueTable>
                    {viewModel.issueItems.map((rowData: IssueItemViewModel) => (
                        <>
                            <IssueOutputDisplayName>{rowData.outputTypeDisplayName}</IssueOutputDisplayName>
                            <OutputDrawingContainer>
                                {rowData.drawings.map((drawing: IDrawing) => (
                                    <OutputDrawing>
                                        <Checkbox checked={viewModel.isIssueSelected(drawing)} onClick={() => viewModel.setIssueSelection(drawing)} />
                                        <DownloadDrawingView
                                            fileName={drawing.drawingFileName}
                                            attachmentUrl={drawing.drawingAttachmentUrl}
                                            inlineUrl={drawing.drawingInlineUrl}
                                            autodeskVersionId={drawing.autodeskVersionId}
                                            drawingId={drawing.drawingId}
                                        />
                                    </OutputDrawing>
                                ))}
                            </OutputDrawingContainer>
                        </>
                    ))}
                    {viewModel.getError("issueItems") && <FormHelperText>{viewModel.getError("issueItems")}</FormHelperText>}
                </IssueTable>
                <DashedSectionSplitter />
                <FormRow grid dc={"1fr"} showIf={viewModel.model.isIssue}>
                    <p style={{ fontSize: "14px" }}>Selected To users: {selectedToProjectDirectoryPersonUserNames}</p>
                    <MultiSelectView
                        style={{ width: "100%", marginBottom: "20px" }}
                        display="To users:"
                        fieldName="selectedToProjectDirectoryPersonUsers"
                        values={viewModel.model.selectedToProjectDirectoryPersonUsers}
                        getOptions={getProjectDirectoryPersonUserOptions}
                        handleChange={(values: number[] | null) => handleSetSelectedToProjectDirectoryPersonUsers(values)}
                    />
                </FormRow>
                <FormRow grid dc={"1fr"} showIf={viewModel.model.isIssue}>
                    <p style={{ fontSize: "14px" }}>Selected CC users: {selectedCCProjectDirectoryPersonUserNames}</p>
                    <MultiSelectView
                        style={{ width: "100%", marginBottom: "20px" }}
                        display="CC users:"
                        fieldName="selectedCCProjectDirectoryPersonUsers"
                        values={viewModel.model.selectedCCProjectDirectoryPersonUsers}
                        getOptions={getProjectDirectoryPersonUserOptions}
                        handleChange={(values: number[] | null) => handleSetSelectedCCProjectDirectoryPersonUsers(values)}
                    />
                </FormRow>
                <FormRow grid dc={"1fr"}>
                    <TextField
                        className="textarea-forminput"
                        InputLabelProps={{ shrink: true }}
                        label="Additional notes:"
                        multiline
                        minRows={5}
                        onBlur={() => viewModel.isFieldValid("notes")}
                        onChange={(event) => viewModel.setValue("notes", event.target.value)}
                        helperText={viewModel.getError("notes")}
                        error={!isEmptyOrWhitespace(viewModel.getError("notes"))}
                        value={viewModel.getValue("notes")}
                    />
                </FormRow>
            </FormModalSection>
            <SnackBar
                messageText={viewModel.snackMessage}
                messageType={viewModel.snackType}
                active={viewModel.snackbarState}
                closeOption={() => viewModel.setSnackbarState(false)}
                autoHideDuration={5000}
            />
        </BaseModal>
    );
});
